import React, { useState, useEffect, useMemo } from 'react';
import BarChart3 from 'lucide-react/dist/esm/icons/bar-chart-3';
import Clock from 'lucide-react/dist/esm/icons/clock';
import FileText from 'lucide-react/dist/esm/icons/file-text';
import MessageSquare from 'lucide-react/dist/esm/icons/message-square';
import Building2 from 'lucide-react/dist/esm/icons/building-2';
import Upload from 'lucide-react/dist/esm/icons/upload';
import History from 'lucide-react/dist/esm/icons/history';
import ArrowRight from 'lucide-react/dist/esm/icons/arrow-right';
import Sparkles from 'lucide-react/dist/esm/icons/sparkles';
import Calendar from 'lucide-react/dist/esm/icons/calendar';
import Target from 'lucide-react/dist/esm/icons/target';
import Sun from 'lucide-react/dist/esm/icons/sun';
import Moon from 'lucide-react/dist/esm/icons/moon';
import Sunrise from 'lucide-react/dist/esm/icons/sunrise';
import TrendingUp from 'lucide-react/dist/esm/icons/trending-up';
import TrendingDown from 'lucide-react/dist/esm/icons/trending-down';
import Play from 'lucide-react/dist/esm/icons/play';
import { ResponsiveBar } from '@nivo/bar';
import { ResponsivePie } from '@nivo/pie';
import { getUserAnalytics } from '../../../services/api';
import { getTranslation } from '../../../i18n';

// Mini sparkline component
const Sparkline = ({ data, color = 'rgb(var(--brand))', height = 20 }) => {
  if (!data || data.length === 0) return null;
  
  const max = Math.max(...data, 1);
  const min = 0;
  const range = max - min || 1;
  const width = 50;
  const points = data.map((value, index) => {
    const x = (index / (data.length - 1 || 1)) * width;
    const y = height - ((value - min) / range) * height;
    return `${x},${y}`;
  }).join(' ');
  
  return (
    <svg width={width} height={height} className="opacity-60">
      <polyline
        fill="none"
        stroke={color}
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
        points={points}
      />
    </svg>
  );
};

const AnalyticsDashboard = ({ onNewAnalysis, onSelectAnalysis, analysesList, language = 'en' }) => {
  const t = (key: string, variables?: Record<string, string | number>) => getTranslation(language, key, variables);
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [animateIn, setAnimateIn] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        const data = await getUserAnalytics();
        if (!cancelled) setAnalytics(data);
      } catch (err) {
        console.error('Failed to fetch analytics:', err);
        if (!cancelled) setError('Failed to load analytics');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    setTimeout(() => setAnimateIn(true), 100);
    return () => { cancelled = true; };
  }, []);

  const fetchAnalytics = async () => {
    try {
      setLoading(true);
      const data = await getUserAnalytics();
      setAnalytics(data);
    } catch (err) {
      console.error('Failed to fetch analytics:', err);
      setError('Failed to load analytics');
    } finally {
      setLoading(false);
    }
  };

  const formatDuration = (seconds) => {
    if (!seconds) return '—';
    if (seconds < 60) return `${Math.round(seconds)}s`;
    const minutes = Math.floor(seconds / 60);
    const secs = Math.round(seconds % 60);
    return `${minutes}m ${secs}s`;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    
    return date.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric' 
    });
  };

  const getGreeting = () => {
    const hour = new Date().getHours();
    if (hour < 12) return { text: t('goodMorning'), icon: Sunrise };
    if (hour < 18) return { text: t('goodAfternoon'), icon: Sun };
    return { text: t('goodEvening'), icon: Moon };
  };

  const greeting = getGreeting();
  const GreetingIcon = greeting.icon;

  // Calculate weekly comparison (current week vs last week) - must be before early returns
  const weeklyComparison = useMemo(() => {
    if (!analytics?.analyses_by_day) return { change: 0, trend: 'neutral' };
    
    const now = new Date();
    const oneWeekAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
    const twoWeeksAgo = new Date(now.getTime() - 14 * 24 * 60 * 60 * 1000);
    
    let thisWeek = 0;
    let lastWeek = 0;
    
    analytics.analyses_by_day.forEach(d => {
      const date = new Date(d.date);
      if (date >= oneWeekAgo) thisWeek += d.count;
      else if (date >= twoWeeksAgo) lastWeek += d.count;
    });
    
    // Handle edge case: both weeks have 0 analyses
    const bothZero = thisWeek === 0 && lastWeek === 0;
    const change = bothZero ? 0 : (lastWeek === 0 ? (thisWeek > 0 ? 100 : 0) : Math.round(((thisWeek - lastWeek) / lastWeek) * 100));
    const trend = bothZero ? 'neutral' : (change > 0 ? 'up' : change < 0 ? 'down' : 'neutral');
    
    return { change: Math.abs(change), trend, thisWeek, lastWeek };
  }, [analytics?.analyses_by_day]);
  
  // Get sparkline data for stats - must be before early returns
  const sparklineData = useMemo(() => {
    if (!analytics?.analyses_by_day) return [];
    return analytics.analyses_by_day.slice(-7).map(d => d.count);
  }, [analytics?.analyses_by_day]);

  if (loading) {
    return (
      <div className="p-2 lg:p-4 w-full h-full max-w-full mx-auto opacity-100 flex flex-col overflow-hidden flex-1 min-h-0">
        <div className="flex flex-col gap-4 flex-1">
          <div className="h-[50px] rounded-xl bg-surface-primary animate-shimmer"></div>
          <div className="grid grid-cols-4 gap-2">
            {[1, 2, 3, 4].map(i => (
              <div key={i} className="h-[60px] rounded-lg bg-surface-primary animate-shimmer"></div>
            ))}
          </div>
          <div className="flex-1 rounded-xl bg-surface-primary animate-shimmer"></div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center gap-4 min-h-[300px] text-status-error">
        <p>{error}</p>
        <button 
          onClick={fetchAnalytics}
          className="px-4 py-2 bg-status-error/10 border border-status-error/30 rounded-lg text-status-error text-xs cursor-pointer transition-all hover:bg-status-error/20"
        >
          Retry
        </button>
      </div>
    );
  }

  // Prepare bar chart data - show last 14 days only
  const barChartData = analytics?.analyses_by_day 
    ? analytics.analyses_by_day.slice(-14).map(d => ({
        date: new Date(d.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
        count: d.count
      }))
    : [];

  // Improved chart colors - more distinct and vibrant
  // chart color — token: brand, cyan, emerald, amber, rose
  const chartColors = ['#8b5cf6', '#06b6d4', '#10b981', '#f59e0b', '#f43f5e'];
  
  const companyPieData = analytics?.analyses_by_company?.slice(0, 5).map((c, i) => ({
    id: c.company,
    label: c.company,
    value: c.count,
    color: chartColors[i]
  })) || [];

  const hasData = analytics?.total_analyses > 0;
  
  // Get last analysis for quick action
  const lastAnalysis = analytics?.recent_activity?.[0];

  return (
    <div className={`p-2 lg:p-4 w-full h-full max-w-full mx-auto flex flex-col overflow-hidden flex-1 min-h-0 transition-all duration-400 ${animateIn ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-2.5'}`}>
      {/* Header with Greeting */}
      {hasData && (
        <div className="flex justify-between items-center py-0.5 mb-1.5 flex-shrink-0">
          <div className="flex items-center gap-2.5">
            <GreetingIcon size={20} className="text-amber-500" />
            <span className="text-lg font-semibold text-slate-100">{greeting.text}!</span>
            <span className="text-slate-600 text-sm">•</span>
            <span className="text-base text-slate-400">{analytics.total_analyses} {analytics.total_analyses !== 1 ? t('documents') : t('document')}</span>
          </div>
          <button 
            className="inline-flex items-center gap-1.5 px-3.5 py-2 bg-gradient-to-br from-violet-500 to-violet-600 text-content-inverse border-none rounded-lg text-xs font-semibold cursor-pointer transition-all duration-300 shadow-[0_4px_15px_rgba(139,92,246,0.35)] hover:-translate-y-0.5 hover:shadow-[0_6px_25px_rgba(139,92,246,0.45)]"
            onClick={onNewAnalysis}
          >
            <Upload size={14} />
            {t('newAnalysis')}
          </button>
        </div>
      )}

      {/* Onboarding Section - Show when no data */}
      {!hasData && (
        <div className="bg-gradient-to-br from-violet-500/10 to-cyan-500/[0.08] border border-violet-500/20 rounded-2xl p-6 mb-4 flex-1 flex flex-col animate-card-slide-in" style={{ animationDelay: '0.15s' }}>
          <div className="text-center mb-5">
            <h2 className="text-xl font-bold text-slate-100 mb-1.5">🚀 {t('getStartedWithQuazi')}</h2>
            <p className="text-slate-400 text-[0.8125rem]">{t('getStartedSubtitle')}</p>
          </div>
          <div className="grid grid-cols-3 gap-4 mb-5 flex-1 max-md:grid-cols-1">
            <div className="bg-surface-primary border border-subtle rounded-xl p-4 relative">
              <div className="w-7 h-7 bg-gradient-to-br from-violet-500 to-violet-600 rounded-full flex items-center justify-center font-bold text-xs text-content-inverse mb-3">1</div>
              <div>
                <h3 className="text-sm font-semibold text-slate-100 mb-1.5">{t('stepOneTitle')}</h3>
                <p className="text-xs text-slate-400 leading-relaxed">{t('stepOneDesc')}</p>
              </div>
              <Upload size={20} className="absolute top-4 right-4 text-violet-500/40" />
            </div>
            <div className="bg-surface-primary border border-subtle rounded-xl p-4 relative">
              <div className="w-7 h-7 bg-gradient-to-br from-violet-500 to-violet-600 rounded-full flex items-center justify-center font-bold text-xs text-content-inverse mb-3">2</div>
              <div>
                <h3 className="text-sm font-semibold text-slate-100 mb-1.5">{t('stepTwoTitle')}</h3>
                <p className="text-xs text-slate-400 leading-relaxed">{t('stepTwoDesc')}</p>
              </div>
              <Sparkles size={20} className="absolute top-4 right-4 text-violet-500/40" />
            </div>
            <div className="bg-surface-primary border border-subtle rounded-xl p-4 relative">
              <div className="w-7 h-7 bg-gradient-to-br from-violet-500 to-violet-600 rounded-full flex items-center justify-center font-bold text-xs text-content-inverse mb-3">3</div>
              <div>
                <h3 className="text-sm font-semibold text-slate-100 mb-1.5">{t('stepThreeTitle')}</h3>
                <p className="text-xs text-slate-400 leading-relaxed">{t('stepThreeDesc')}</p>
              </div>
              <MessageSquare size={20} className="absolute top-4 right-4 text-violet-500/40" />
            </div>
          </div>
          <div className="text-center">
            <button 
              className="inline-flex items-center gap-1.5 px-4 py-2.5 bg-gradient-to-br from-violet-500 to-violet-600 border-none rounded-lg text-content-inverse text-xs font-semibold cursor-pointer transition-all duration-300 shadow-[0_4px_15px_rgba(139,92,246,0.3)] hover:-translate-y-0.5 hover:shadow-[0_8px_25px_rgba(139,92,246,0.4)]"
              onClick={onNewAnalysis}
            >
              <Upload size={14} />
              {t('uploadFirstDocument')}
              <ArrowRight size={12} />
            </button>
          </div>
        </div>
      )}

      {/* Stats Grid */}
      <div className="grid grid-cols-4 gap-1.5 mb-1.5 flex-shrink-0 max-lg:grid-cols-2 max-sm:grid-cols-2">
        {/* Total Analyses */}
        <div 
          className="stat-card flex items-center gap-2.5 px-3 py-2 bg-slate-900/60 border border-subtle rounded-lg backdrop-blur-xl relative overflow-hidden animate-card-slide-in" 
          style={{ animationDelay: '0.1s' }}
          title={t('tooltipTotalAnalyses')}
        >
          <div className="absolute top-0 left-0 right-0 h-[3px] bg-gradient-to-r from-violet-500 to-transparent opacity-70"></div>
          <div className="w-[30px] h-[30px] flex items-center justify-center rounded-md bg-violet-500/[0.15] text-violet-500 flex-shrink-0">
            <FileText size={15} />
          </div>
          <div className="flex flex-col gap-0.5 flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <span className="text-lg font-bold text-slate-100 leading-tight">{analytics?.total_analyses || 0}</span>
              {sparklineData.length > 0 && <Sparkline data={sparklineData} color="rgb(var(--brand))" />}
            </div>
            <span className="text-[0.6875rem] text-slate-500 uppercase tracking-wide">{t('totalAnalyses')}</span>
          </div>
        </div>

        {/* This Week with comparison */}
        <div 
          className="stat-card flex items-center gap-2.5 px-3 py-2 bg-slate-900/60 border border-subtle rounded-lg backdrop-blur-xl relative overflow-hidden animate-card-slide-in" 
          style={{ animationDelay: '0.15s' }}
          title={t('tooltipThisWeek')}
        >
          <div className="absolute top-0 left-0 right-0 h-[3px] bg-gradient-to-r from-cyan-500 to-transparent opacity-70"></div>
          <div className="w-[30px] h-[30px] flex items-center justify-center rounded-md bg-cyan-500/[0.15] text-cyan-500 flex-shrink-0">
            <Calendar size={15} />
          </div>
          <div className="flex flex-col gap-0.5 flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <span className="text-lg font-bold text-slate-100 leading-tight">{analytics?.analyses_this_week || 0}</span>
              {weeklyComparison.trend !== 'neutral' ? (
                <span className={`flex items-center gap-0.5 text-[10px] font-semibold px-1.5 py-0.5 rounded-full ${
                  weeklyComparison.trend === 'up' ? 'bg-emerald-500/20 text-emerald-400' : 'bg-status-error/20 text-status-error'
                }`}>
                  {weeklyComparison.trend === 'up' ? <TrendingUp size={10} /> : <TrendingDown size={10} />}
                  {weeklyComparison.change}%
                </span>
              ) : weeklyComparison.thisWeek === 0 && weeklyComparison.lastWeek === 0 ? (
                <span className="flex items-center gap-0.5 text-[10px] font-semibold px-1.5 py-0.5 rounded-full bg-slate-500/20 text-slate-400">
                  —
                </span>
              ) : null}
            </div>
            <span className="text-[0.6875rem] text-slate-500 uppercase tracking-wide">{t('thisWeek')}</span>
          </div>
        </div>

        {/* Avg Duration */}
        <div 
          className="stat-card flex items-center gap-2.5 px-3 py-2 bg-slate-900/60 border border-subtle rounded-lg backdrop-blur-xl relative overflow-hidden animate-card-slide-in" 
          style={{ animationDelay: '0.2s' }}
          title={t('tooltipAvgDuration')}
        >
          <div className="absolute top-0 left-0 right-0 h-[3px] bg-gradient-to-r from-emerald-500 to-transparent opacity-70"></div>
          <div className="w-[30px] h-[30px] flex items-center justify-center rounded-md bg-emerald-500/[0.15] text-emerald-500 flex-shrink-0">
            <Clock size={15} />
          </div>
          <div className="flex flex-col gap-0.5 flex-1 min-w-0">
            <span className="text-lg font-bold text-slate-100 leading-tight">{formatDuration(analytics?.avg_analysis_duration_seconds)}</span>
            <span className="text-[0.6875rem] text-slate-500 uppercase tracking-wide">{t('avgDuration')}</span>
          </div>
        </div>

        {/* Quick Action - View Last Analysis OR Chat Messages */}
        {lastAnalysis ? (
          <div 
            className="stat-card flex items-center gap-2.5 px-3 py-2 bg-gradient-to-br from-violet-500/10 to-cyan-500/10 border border-violet-500/20 rounded-lg backdrop-blur-xl transition-all duration-300 relative overflow-hidden hover:border-violet-500/40 hover:-translate-y-0.5 hover:shadow-[0_8px_25px_rgba(139,92,246,0.2)] animate-card-slide-in cursor-pointer card-lift-interactive" 
            style={{ animationDelay: '0.25s' }}
            onClick={() => onSelectAnalysis && onSelectAnalysis(lastAnalysis.analysis_id)}
          >
            <div className="absolute top-0 left-0 right-0 h-[3px] bg-gradient-to-r from-violet-500 via-cyan-500 to-transparent opacity-70"></div>
            <div className="w-[30px] h-[30px] flex items-center justify-center rounded-md bg-violet-500/20 text-violet-400 flex-shrink-0">
              <Play size={15} />
            </div>
            <div className="flex flex-col gap-0.5 flex-1 min-w-0">
              <span className="text-xs font-semibold text-slate-100 leading-tight truncate">{lastAnalysis.company_name || t('lastAnalysis')}</span>
              <span className="text-[0.6rem] text-violet-400 uppercase tracking-wide flex items-center gap-1">
                {t('continueAction')} <ArrowRight size={10} />
              </span>
            </div>
          </div>
        ) : (
          <div 
            className="stat-card flex items-center gap-2.5 px-3 py-2 bg-slate-900/60 border border-subtle rounded-lg backdrop-blur-xl relative overflow-hidden animate-card-slide-in" 
            style={{ animationDelay: '0.25s' }}
            title={t('tooltipChatMessages')}
          >
            <div className="absolute top-0 left-0 right-0 h-[3px] bg-gradient-to-r from-amber-500 to-transparent opacity-70"></div>
            <div className="w-[30px] h-[30px] flex items-center justify-center rounded-md bg-amber-500/[0.15] text-amber-500 flex-shrink-0">
              <MessageSquare size={15} />
            </div>
            <div className="flex flex-col gap-0.5 flex-1 min-w-0">
              <span className="text-lg font-bold text-slate-100 leading-tight">{analytics?.total_chat_messages || 0}</span>
              <span className="text-[0.6875rem] text-slate-500 uppercase tracking-wide">{t('chatMessages')}</span>
            </div>
          </div>
        )}
      </div>

      {/* Main Content Grid */}
      <div className="grid grid-cols-2 grid-rows-[minmax(80px,1fr)_minmax(70px,0.85fr)] gap-1.5 flex-1 min-h-0 max-lg:grid-cols-1 max-lg:grid-rows-[1fr_auto_auto]">
        {/* Activity Chart */}
        <div className="col-span-2 row-start-1 bg-slate-900/60 border border-subtle rounded-lg backdrop-blur-xl p-2 flex flex-col overflow-hidden min-h-0 animate-card-slide-in max-lg:col-span-1" style={{ animationDelay: '0.3s' }}>
          <div className="flex justify-between items-center pb-1 mb-1 border-b border-subtle flex-shrink-0">
            <h3 className="flex items-center gap-2 text-[0.8125rem] font-semibold text-slate-200 m-0 uppercase tracking-wide">
              <BarChart3 size={16} className="opacity-80" /> {t('analysisActivity')}
            </h3>
            <span className="text-[0.6875rem] text-slate-500 uppercase tracking-wide">{t('lastFourteenDays')}</span>
          </div>
          <div className="flex-1 min-h-0">
            {hasData && barChartData.length > 0 ? (
              <ResponsiveBar
                data={barChartData}
                keys={['count']}
                indexBy="date"
                margin={{ top: 10, right: 10, bottom: 40, left: 35 }}
                padding={0.4}
                valueScale={{ type: 'linear' }}
                indexScale={{ type: 'band', round: true }}
                colors={['#8b5cf6']} // chart color — token: brand
                borderRadius={4}
                axisTop={null}
                axisRight={null}
                axisBottom={{
                  tickSize: 0,
                  tickPadding: 8,
                  tickRotation: -45,
                }}
                axisLeft={{
                  tickSize: 0,
                  tickPadding: 8,
                  tickValues: 5,
                }}
                enableGridY={true}
                gridYValues={5}
                enableLabel={false}
                theme={{
                  axis: {
                    ticks: { text: { fill: '#94a3b8', fontSize: 11 } }
                  },
                  grid: { line: { stroke: '#334155', strokeWidth: 1 } }
                }}
                tooltip={({ indexValue, value }) => (
                  <div className="bg-surface-page px-3 py-2 rounded-lg border border-violet-500/30 shadow-[0_4px_20px_rgba(0,0,0,0.4)] flex flex-col gap-0.5">
                    <strong className="text-slate-100 text-[0.6875rem]">{indexValue}</strong>
                    <span className="text-slate-400 text-[0.625rem]">{value} {value !== 1 ? t('analysesCount') : t('analysisCount')}</span>
                  </div>
                )}
              />
            ) : (
              <div className="h-full flex flex-col items-center justify-center text-slate-600 gap-2">
                <BarChart3 size={36} className="opacity-30" />
                <p className="text-[0.6875rem] m-0 text-center">{t('activityChartEmpty')}</p>
              </div>
            )}
          </div>
        </div>

        {/* Company Breakdown */}
        <div className="col-start-1 row-start-2 bg-slate-900/60 border border-subtle rounded-lg backdrop-blur-xl p-2 flex flex-col overflow-hidden min-h-0 animate-card-slide-in max-lg:col-start-1 max-lg:row-start-2" style={{ animationDelay: '0.35s' }}>
          <div className="flex justify-between items-center pb-1 mb-1 border-b border-subtle flex-shrink-0">
            <h3 className="flex items-center gap-2 text-[0.8125rem] font-semibold text-slate-200 m-0 uppercase tracking-wide">
              <Building2 size={14} className="opacity-80" /> {t('companiesAnalyzed')}
            </h3>
            <span className="text-[0.6875rem] text-slate-500 uppercase tracking-wide">{analytics?.analyses_by_company?.length || 0} {t('unique')}</span>
          </div>
          <div className="flex flex-1 gap-2 min-h-0 items-stretch">
            <div className="flex-[0_0_55%] min-h-[60px]">
              {companyPieData.length > 0 ? (
                <ResponsivePie
                  data={companyPieData}
                  margin={{ top: 10, right: 10, bottom: 10, left: 10 }}
                  innerRadius={0.6}
                  padAngle={2}
                  cornerRadius={4}
                  activeOuterRadiusOffset={6}
                  colors={{ datum: 'data.color' }}
                  borderWidth={0}
                  enableArcLinkLabels={false}
                  arcLabelsSkipAngle={45}
                  arcLabelsTextColor="#ffffff"
                  theme={{
                    labels: { text: { fontSize: 11, fontWeight: 600 } }
                  }}
                  tooltip={({ datum }) => (
                    <div className="bg-surface-page px-3 py-2 rounded-lg border border-violet-500/30 shadow-[0_4px_20px_rgba(0,0,0,0.4)] flex flex-col gap-0.5">
                      <strong className="text-slate-100 text-[0.6875rem]">{datum.label}</strong>
                      <span className="text-slate-400 text-[0.625rem]">{datum.value} {datum.value !== 1 ? t('analysesCount') : t('analysisCount')}</span>
                    </div>
                  )}
                />
              ) : (
                <div className="h-full flex flex-col items-center justify-center text-slate-600 gap-2">
                  <Building2 size={32} className="opacity-30" />
                  <p className="text-[0.6875rem] m-0">{t('companyBreakdownEmpty')}</p>
                </div>
              )}
            </div>
            {companyPieData.length > 0 && (
              <div className="flex-1 flex flex-col gap-1 justify-center overflow-y-auto pr-1">
                {companyPieData.map((c, i) => (
                  <div key={i} className="flex items-center gap-1.5 text-[0.6875rem] text-slate-400 px-1.5 py-1 transition-all duration-200 rounded hover:bg-violet-500/10 cursor-default" title={c.label}>
                    <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ background: c.color }}></span>
                    <span className="flex-1 whitespace-nowrap overflow-hidden text-ellipsis max-w-[100px]">{c.label}</span>
                    <span className="font-semibold text-slate-100 flex-shrink-0">{c.value}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Recent Activity Timeline */}
        <div className="col-start-2 row-start-2 bg-slate-900/60 border border-subtle rounded-lg backdrop-blur-xl p-2 flex flex-col overflow-hidden min-h-0 animate-card-slide-in max-lg:col-start-1 max-lg:row-start-3" style={{ animationDelay: '0.4s' }}>
          <div className="flex justify-between items-center pb-1 mb-1 border-b border-subtle flex-shrink-0">
            <h3 className="flex items-center gap-2 text-[0.8125rem] font-semibold text-slate-200 m-0 uppercase tracking-wide">
              <History size={14} className="opacity-80" /> {t('recentActivity')}
            </h3>
            <span className="text-[0.6875rem] text-slate-500 uppercase tracking-wide">{t('latestAnalyses')}</span>
          </div>
          <div className="flex-1 overflow-y-auto min-h-0">
            {analytics?.recent_activity?.length > 0 ? (
              <div className="flex flex-col gap-0.5">
                {analytics.recent_activity.slice(0, 5).map((activity, idx) => (
                  <div 
                    key={activity.analysis_id} 
                    className="flex items-center gap-1.5 px-1.5 py-1 rounded cursor-pointer transition-all duration-200 bg-surface-primary border border-transparent hover:bg-violet-500/10 hover:border-violet-500/20 group relative"
                    onClick={() => onSelectAnalysis && onSelectAnalysis(activity.analysis_id)}
                  >
                    <div className="w-1.5 h-1.5 bg-gradient-to-br from-violet-500 to-cyan-500 rounded-full flex-shrink-0"></div>
                    <div className="flex-1 min-w-0 flex flex-col">
                      <span className="text-[0.625rem] font-medium text-slate-200 whitespace-nowrap overflow-hidden text-ellipsis">
                        {activity.display_name || activity.document_name}
                      </span>
                      <span className="text-[0.5rem] text-slate-500">{formatDate(activity.completed_at || activity.created_at)}</span>
                    </div>
                    <ArrowRight size={12} className="text-slate-600 opacity-0 transition-all duration-200 group-hover:opacity-100 group-hover:translate-x-0.5 group-hover:text-violet-500" />
                  </div>
                ))}
              </div>
            ) : (
              <div className="h-full flex flex-col items-center justify-center text-slate-600 gap-2">
                <History size={36} className="opacity-30" />
                <p className="text-[0.6875rem] m-0">{t('recentActivityEmpty')}</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Industries & Insights */}
      {analytics?.industries_analyzed?.length > 0 && (
        <div className="flex items-center gap-2 py-1 mt-0.5 flex-shrink-0 animate-card-slide-in max-sm:flex-col max-sm:items-start max-sm:gap-1.5" style={{ animationDelay: '0.5s' }}>
          <h4 className="flex items-center gap-1.5 text-[0.6875rem] font-semibold text-slate-500 m-0 uppercase tracking-wide whitespace-nowrap">
            <Target size={14} /> {t('industriesAnalyzed')}
          </h4>
          <div className="flex flex-wrap gap-1.5">
            {analytics.industries_analyzed.map((industry, idx) => (
              <span 
                key={idx} 
                className="px-2.5 py-1 bg-violet-500/10 border border-violet-500/20 rounded-full text-[0.6875rem] text-violet-400 relative overflow-hidden"
              >
                {industry}
              </span>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default AnalyticsDashboard;
